import React from "react";
import { Check, Sparkles, Crown, Clock } from "lucide-react";

const APP_URL = "https://app.fittrackcoach.in";

/**
 * Three-tier pricing matching the actual app.fittrackcoach.in/pricing page.
 * Prices shown as discounted launch rates.
 */
const PLANS = [
  {
    key: "starter",
    name: "Starter",
    tagline: "Perfect to get started",
    price: "₹0",
    period: "forever",
    clients: "10 clients",
    cta: "Start Free",
    ctaStyle: "ghost",
    description: "Start managing your first 10 clients — for free.",
    features: [
      { text: "Full client profile management", enabled: true },
      { text: "Before / after photo gallery", enabled: true },
      { text: "Weight & measurement tracking", enabled: true },
      { text: "Progress charts & graphs", enabled: true },
      { text: "Weekly workout plans", enabled: true },
      { text: "Client portal (magic links)", enabled: true },
      { text: "Basic analytics", enabled: true },
      { text: "PDF reports", enabled: false },
      { text: "Diet plan management", enabled: false },
    ],
  },
  {
    key: "pro",
    name: "Pro",
    tagline: "Perfect for growing trainers",
    price: "₹599",
    strikePrice: "₹799",
    discount: "25% OFF",
    period: "/month",
    clients: "50 clients",
    cta: "Go Pro & Remove Limits",
    ctaStyle: "glow",
    description: "Handle up to 50 clients without chaos.",
    badge: "Most popular",
    highlighted: true,
    features: [
      { text: "Everything in Starter", enabled: true },
      { text: "Up to 50 clients", enabled: true },
      { text: "Advanced analytics dashboard", enabled: true },
      { text: "Email support", enabled: true },
      { text: "PDF progress reports", enabled: true, soon: true },
      { text: "Diet plan management", enabled: true, soon: true },
      { text: "Bulk client import (CSV)", enabled: true, soon: true },
    ],
  },
  {
    key: "elite",
    name: "Elite",
    tagline: "Built for serious income growth",
    price: "₹999",
    strikePrice: "₹1499",
    discount: "33% OFF",
    period: "/month",
    clients: "200 clients",
    cta: "Go Elite",
    ctaStyle: "elite",
    description: "Scale your coaching business to 200+ clients.",
    badge: "Best value",
    features: [
      { text: "Everything in Pro", enabled: true },
      { text: "Up to 200 clients", enabled: true },
      { text: "Priority support", enabled: true },
      { text: "Dedicated account manager", enabled: true },
      { text: "Push notifications", enabled: true, soon: true },
      { text: "Client attendance tracking", enabled: true, soon: true },
      { text: "Revenue & payment tracking", enabled: true, soon: true },
    ],
  },
];

export default function Pricing() {
  return (
    <section id="pricing" className="py-20 md:py-28 relative" data-testid="pricing-section">
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, rgba(34,211,238,0.05), transparent 50%)",
        }}
      />
      <div className="section-container relative">
        <div className="text-center max-w-2xl mx-auto mb-14 reveal">
          <div className="chip mb-4">Pricing</div>
          <h2 className="font-display text-4xl md:text-5xl leading-tight">
            Simple plans. <span className="gradient-text">Real growth.</span>
          </h2>
          <p className="mt-4 text-white/60">
            Start free. Upgrade when you're ready to scale. Founder pricing — locked in forever.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
          {PLANS.map((plan) => (
            <PlanCard key={plan.key} plan={plan} />
          ))}
        </div>

        <div className="mt-8 text-center text-sm text-white/45 reveal">
          All prices in INR · Cancel anytime · Takes less than 2 minutes to start
        </div>
      </div>
    </section>
  );
}

function PlanCard({ plan }) {
  const isPro = plan.highlighted;
  const isElite = plan.key === "elite";

  return (
    <div
      className={`relative rounded-2xl p-6 md:p-7 reveal flex flex-col ${
        isPro ? "pricing-card-pro" : "card-surface"
      }`}
      data-testid={`pricing-card-${plan.key}`}
    >
      {plan.badge && (
        <div
          className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-bold inline-flex items-center gap-1 ${
            isPro
              ? "btn-primary-glow text-[#050814]"
              : "bg-amber-400/90 text-[#2a1d00] border border-amber-300/40"
          }`}
        >
          {isPro ? <Sparkles size={11} /> : <Crown size={11} />} {plan.badge}
        </div>
      )}

      <div className="relative flex flex-col flex-1">
        <div className="text-sm text-white/55">{plan.tagline}</div>
        <h3 className="text-2xl font-bold text-white mt-1">{plan.name}</h3>

        {/* Price row */}
        <div className="mt-5">
          {plan.strikePrice && (
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm text-white/40 line-through">{plan.strikePrice}</span>
              <span className="text-[10px] font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                {plan.discount}
              </span>
            </div>
          )}
          <div className="flex items-baseline gap-1">
            <span className="font-display text-5xl md:text-6xl text-white leading-none">
              {plan.price}
            </span>
            <span className="text-sm text-white/50">{plan.period}</span>
          </div>
          <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-cyan-300 font-semibold">
            <Clock size={12} /> {plan.clients}
          </div>
          <p className="mt-3 text-sm text-white/55">{plan.description}</p>
        </div>

        {/* CTA */}
        <a
          href={APP_URL}
          className={`mt-6 block text-center rounded-xl px-5 py-3.5 text-sm font-bold w-full ${
            plan.ctaStyle === "glow"
              ? "btn-primary-glow"
              : plan.ctaStyle === "elite"
              ? "bg-amber-400 hover:bg-amber-300 text-[#2a1d00] transition-colors"
              : "btn-ghost"
          }`}
          data-testid={`pricing-cta-${plan.key}`}
        >
          {plan.cta}
        </a>

        {/* Features */}
        <ul className="mt-6 space-y-3 flex-1">
          {plan.features.map((f, i) => (
            <li
              key={i}
              className={`flex items-start gap-2.5 text-sm ${
                f.enabled ? "text-white/80" : "text-white/35 line-through"
              }`}
            >
              <Check
                size={16}
                className={
                  f.enabled
                    ? isPro
                      ? "text-cyan-300 mt-0.5 shrink-0"
                      : isElite
                      ? "text-amber-300 mt-0.5 shrink-0"
                      : "text-white/60 mt-0.5 shrink-0"
                    : "text-white/25 mt-0.5 shrink-0"
                }
              />
              <span className="flex-1">
                {f.text}
                {f.soon && (
                  <span className="ml-2 text-[9.5px] font-bold text-amber-300 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded-full uppercase tracking-wider align-middle">
                    Coming soon
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
